<template>
  <div ref="content" class="content-item radius-1-2">
    <div v-if="loaded && accommodation">
      <div class="row">
        <div class="col-12">
          <figure class="figure-map radius-1-2">
            <picture v-if="accommodation.hasImage(false)">
              <source :srcset="accommodation.getTopImage().getTyrionSrc('thumb_mw', 'webp')" type="image/webp">
              <source :srcset="accommodation.getTopImage().getTyrionSrc('thumb_mw')"
                      :type="'image/' + accommodation.getTopImage().type">
              <img :src="accommodation.getTopImage().getTyrionSrc('thumb_mw')"
                   :alt="accommodation.title"
                   class="img-cover img-fluid radius-1-2" loading="lazy"
                   :width="accommodation.getTopImage().seoWidth('thumb_m')"
                   :height="accommodation.getTopImage().seoHeight('thumb_m')">
            </picture>
          </figure>
          <div class="col-12 p-0 text-center">
            <NuxtLink :to="`/${activeLocale.code}-${activeCountry.code}/${accommodation.link}`"
                      target="_href"
                      :title="accommodation.title" @click="trackClick">
              <h3 class="pointer h5 mt-2 mb-0 text-dark ">{{ accommodation.title }}</h3>
              <span class="text-muted">{{ $t(`acc-type-${accommodation.category}`) }}, max
              {{
                  accommodation.max_hosts_capacity
                }}&nbsp;{{ $t($vysklonujSlovo('host', accommodation.max_hosts_capacity, 'ia', 'i')) }}
              </span>

              <p class="mt-3 h5 mb-0 first-color"
                 v-if="accommodation.price_from > 0 && accommodation.price_from !== 9999">
                <small v-if="tripDuration === 0" class="text-silver me-1">{{ $t('from') }}</small>
                <strong>{{
                    accommodation.price_from
                  }}{{ switchCurrencyMark(accommodation.main_currency, props.activeLocale.code) }}
                </strong>&nbsp;/&nbsp;{{ $t('night') }}
              </p>
              <p class="mt-3 h5 mb-0 first-color" v-if="tripDuration > 0 && accommodation.price_from != 9999">
                {{ totalTripPrice }}€ {{ $t('total') }}
              </p>

              <p class="mt-3 mb-0">
                <button class="btn btn-outline-first-b btn-sm w-100 rounded" type="button">
                  {{ $t('show-more') }}
                </button>
              </p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
    <div class="alert alert-danger" v-if="error && loaded">
      {{ $t('unable-to-load-accommodation') }}
    </div>
    <div v-else-if="!loaded">
      {{ $t('loading') }}...
    </div>
    <PartsTempPreloader v-if="loading" height="200px" :class="'top-40'"/>
  </div>
</template>

<script setup lang="ts">
import {computed, PropType, ref, watch} from "vue";
import dayjs from "dayjs";
import {useRoute, useNuxtApp} from "#app";
import Accommodation from "../../../../../lib/models/Accommodation";
import {GATracker} from "../../../../../composables/ga_tracker";
import {switchCurrencyMark} from "../../../../../lib/functions";

const props = defineProps({
  id: Number,
  activeLocale: Object as PropType<{ code: string }>,
  activeCountry: Object as PropType<{ code: string }>
});

const loaded = ref(false);
const loading = ref(false);
const accommodation = ref(null);
const error = ref(false);

const {$tyrionApi} = useNuxtApp();
const {GATrackEvent} = GATracker();

const params = computed(() => {
  return useRoute().query;
});

const isTopOffer = computed(() => {
  return accommodation.value.id === 5
});

const totalTripPrice = computed(() => {
  const totalPrice = accommodation.value.price_from;
  const duration = tripDuration.value;
  return !isNaN(totalPrice) && duration > 0 ? (Math.round(totalPrice * duration)) : false;
});

const tripDuration = computed(() => {
  if (params.value && params.value.dateFrom && params.value.dateTo) {
    return Number(dayjs(params.value.dateTo).diff(dayjs(params.value.dateFrom), 'day'));
  }
  return 0;
});

watch(() => props.id, () => {
  loadAccommodation();
});

const trackClick = () => {
  GATrackEvent({
    event: 'map_marker_popup_redirect',
    action: 'click',
    label: 'Redirect to accommodation from popup',
    value: accommodation.value.link,
    items: [
      {
        item_id: accommodation.value.id,
        item_name: accommodation.value.title,
        item_category: accommodation.value.category,
        item_variant: accommodation.value.location.tourist_region.code,
      }
    ]
  }, 0);
}

const loadAccommodation = () => {
  if (props.id > 0) {
    loading.value = true;
    $tyrionApi.postTyrionUrl(`/api3/accommodation/filter`, {body: {filter: {id: props.id}}}).then(response => {
      if (response.status === 'success') {
        accommodation.value = (new Accommodation()).fromObject(response.data.objects[0]);
        loaded.value = true;
      } else {
        error.value = true;
        loaded.value = true;
      }
    }).catch(error => {
      error.value = true;
      loaded.value = true;
    }).finally(() => {
      setTimeout(() => {
        loading.value = false;
      }, 500);
    });
  } else {
    loaded.value = false;
  }
}
</script>


